/**
 * Authentication Context
 * Provides auth state and methods throughout the app
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { authService, subscriptionService } from '../api/services';
import { storage } from '../utils/storage';
import { analytics } from '../services/analytics';
import type { User, LoginRequest, RegisterRequest } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  hideSampleDashboard: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = storage.getAccessToken();
      const savedUser = storage.getUser();

      if (token && savedUser) {
        setUser(savedUser);

        // Fetch fresh subscription data in background
        try {
          const subscription = await subscriptionService.getCurrentSubscription();
          const updatedUser = { ...savedUser, subscription };
          setUser(updatedUser);
          storage.setUser(updatedUser);
          analytics.identify(updatedUser);
        } catch (error) {
          // Ignore subscription fetch errors during init
          console.log('Could not refresh subscription on init');
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (data: LoginRequest) => {
    try {
      const response = await authService.login(data);

      // Save tokens and user
      storage.setAccessToken(response.access_token);
      storage.setRefreshToken(response.refresh_token);
      storage.setUser(response.user);
      analytics.identify(response.user);

      setUser(response.user);
      toast.success('Login successful!');
      // Navigation handled by the component
    } catch (error: any) {
      let message = 'Login failed';

      if (error.response?.status === 401) {
        message = 'Invalid email or password';
      } else if (error.response?.status === 422) {
        message = 'Please check your input and try again';
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.message) {
        message = error.message;
      }

      toast.error(message);
      throw error;
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      const response = await authService.register(data);
      
      // Save tokens and user
      storage.setAccessToken(response.access_token);
      storage.setRefreshToken(response.refresh_token);
      storage.setUser(response.user);
      analytics.identify(response.user);

      setUser(response.user);
      toast.success('Registration successful!');
      // Navigation handled by the component
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      throw error;
    }
  };

  const loginWithGoogle = async (credential: string) => {
    try {
      const response = await authService.googleLogin({ credential });

      // Save tokens and user
      storage.setAccessToken(response.access_token);
      storage.setRefreshToken(response.refresh_token);
      storage.setUser(response.user);
      analytics.identify(response.user);

      setUser(response.user);
      toast.success('Login successful!');
      // Navigation handled by the component
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Google sign-in failed';
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    // Check if we're in an impersonation session and restore admin session
    const adminToken = localStorage.getItem('admin_original_token');
    if (adminToken) {
      // Restore admin session
      storage.setAccessToken(adminToken);
      localStorage.removeItem('admin_original_token');
      // Fetch admin user data
      authService.getCurrentUser().then((adminUser) => {
        subscriptionService.getCurrentSubscription().then((subscription) => {
          const userWithSubscription = { ...adminUser, subscription };
          storage.setUser(userWithSubscription);
          setUser(userWithSubscription);
          toast.success('Ended impersonation session');
          window.location.href = '/admin/users';
        }).catch(() => {
          storage.setUser(adminUser);
          setUser(adminUser);
          toast.success('Ended impersonation session');
          window.location.href = '/admin/users';
        });
      }).catch(() => {
        // If fetching admin user fails, just clear everything
        storage.clearAuth();
        setUser(null);
        toast.success('Logged out successfully');
        window.location.href = '/login';
      });
    } else {
      // Regular logout
      storage.clearAuth();
      analytics.reset();
      setUser(null);
      toast.success('Logged out successfully');
      window.location.href = '/login';
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authService.getCurrentUser();
      // Also fetch subscription
      try {
        const subscription = await subscriptionService.getCurrentSubscription();
        const userWithSubscription = { ...response, subscription };
        storage.setUser(userWithSubscription);
        setUser(userWithSubscription);
      } catch {
        storage.setUser(response);
        setUser(response);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const refreshSubscription = async () => {
    if (!user) return;
    try {
      const subscription = await subscriptionService.getCurrentSubscription();
      const updatedUser = { ...user, subscription };
      storage.setUser(updatedUser);
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to refresh subscription:', error);
    }
  };

  const hideSampleDashboard = async () => {
    if (!user) return;
    const updatedUser = { ...user, show_sample_dashboard: false };
    // Optimistic update: hide immediately, then persist.
    storage.setUser(updatedUser);
    setUser(updatedUser);
    try {
      await authService.updateCurrentUser({ show_sample_dashboard: false });
    } catch (error) {
      console.error('Failed to persist sample dashboard preference:', error);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAdmin: user?.is_admin || false,
    login,
    register,
    loginWithGoogle,
    logout,
    refreshUser,
    refreshSubscription,
    hideSampleDashboard,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

